// src/services/datev/DatevEauService.ts
//
// Service-Layer für die DATEV hr:eau API (elektronische Arbeitsunfähigkeitsbescheinigung).
// 4 Endpoints: Berechtigungsprüfung, Anfrage erstellen, Feedback abrufen, Anfrage stornieren.

import { netlifyFetch } from '../../shared/services/netlifyFetch';

// ============================================
// TYPESCRIPT INTERFACES (basierend auf hr:eau OpenAPI v1.0.23)
// ============================================

export type DatevEauSource = 'LUG' | 'lug' | 'LODAS' | 'lodas';

/** Flag für aktuellen AU-Status (laut OpenAPI-Spec) */
export type DatevEauFlag = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export interface DatevEauNotification {
  email?: string;
}

export interface DatevEauContactPerson {
  name: string;                    // Pflicht
  telephone: string;               // Pflicht
  company_name: string;            // Pflicht
  postal_code: string;             // Pflicht
  city: string;                    // Pflicht
  street: string;                  // Pflicht
  house_number?: string;
  fax?: string;
  email?: string;
  gender?: 'M' | 'W' | 'X' | 'D';  // deprecated laut Spec
}

/** POST Body für neue eAU-Anfrage */
export interface DatevEauRequestBody {
  source: DatevEauSource;          // Pflicht
  start_work_incapacity: string;   // Pflicht, YYYY-MM-DD
  notification?: DatevEauNotification;
  contact_person?: DatevEauContactPerson;
  follow_up_certification?: boolean;
  collaboration_identifier?: string;
  /** XTAX-intern: Bestätigung §5 EntgFG (wird nicht an DATEV gesendet) */
  __compliance_confirmed?: boolean;
  /**
   * XTAX-intern: Kennung des Krankheitszeitraums, zu dem diese Anfrage gehört
   * (wird nicht an DATEV gesendet).
   *
   * Der Anforderungsdatensatz kennt kein Enddatum, die Kasse antwortet deshalb
   * immer nur mit dem AU-Zeitraum rund um das angefragte Startdatum. Über
   * diese Kennung hängt die Anfrage an einem bei uns gespeicherten Zeitraum,
   * gegen den die Rückmeldung abgeglichen wird. Deckt sie ihn nicht ab, fasst
   * der nächtliche Lauf selbständig nach.
   */
  __absence_finding_id?: string;
}

/** Antwort auf POST (erweitert um eau_uuid und location vom Backend) */
export interface DatevEauPostResponse {
  eau_uuid: string;                // vom Backend aus Location-Header extrahiert
  location: string;                // Original-Location-Header
  error?: string;                  // Warning bei 201
  error_description?: string;
  additional_messages?: DatevEauAdditionalMessage[];
}

export interface DatevEauAdditionalMessage {
  id: string;                      // z.B. "EAU C-PL-0000001"
  description: string;
  severity: 'error' | 'warning' | 'info' | string;
  affected_fields?: string[];
}

/** Feedback-Kontaktperson der Krankenkasse */
export interface DatevEauContactPersonHealthInsurance {
  name: string;
  telephone: string;
  name1_health_insurance: string;
  postal_code: string;
  city: string;
  fax?: string;
  email?: string;
  street?: string;
  house_number?: string;
  gender_contact_person?: 'M' | 'W' | 'X' | 'D' | null;   // deprecated laut Spec
  name2_health_insurance?: string | null;                 // deprecated laut Spec
  name3_health_insurance?: string | null;                 // deprecated laut Spec
}

/** IncapacityForWork — AU-Details aus Feedback */
export interface DatevEauIncapacityForWork {
  start_work_incapacity_employer: string;       // Pflicht, YYYY-MM-DD
  start_work_incapacity_au?: string;
  end_work_incapacity_au?: string;
  actual_end_work_incapacity_au?: string | null;
  date_of_diagnosis?: string;
  flag_current_work_incapacity?: DatevEauFlag;
  accident_at_work?: boolean;
  assignment_accident_insurance_doctor?: boolean;
  other_accident?: boolean;
  start_hospitalisation?: string | null;        // deprecated laut Spec, wird trotzdem angezeigt
  end_hospitalisation?: string | null;          // deprecated laut Spec, wird trotzdem angezeigt
  initial_certificate?: boolean;
  automatic_feedback_until?: string | null;
}

/** ErrorBlock aus Feedback */
export interface DatevEauErrorBlock {
  origin?: 'INTERN' | 'EXTERN' | string;
  error_number?: string;           // z.B. "DXAUv20"
  error_text?: string;
  error_value?: string;
}

/** Einzelnes Feedback von Krankenkasse */
export interface DatevEauFeedbackFromHealthInsurance {
  guid: string;                    // Pflicht
  contact_person?: DatevEauContactPersonHealthInsurance;
  incapacity_for_work?: DatevEauIncapacityForWork;
  error_block_list?: DatevEauErrorBlock[] | null;
}

/** GET /feedbacks Response */
export interface DatevEauFeedback {
  source: DatevEauSource;          // Pflicht
  start_work_incapacity: string;   // Pflicht
  collaboration_identifier?: string;
  feedbacks_from_health_insurance?: DatevEauFeedbackFromHealthInsurance[];
}

/** API-Fehlerformat (vom Backend durchgereicht) */
export interface DatevEauApiError {
  error: string;
  datev_error?: string;
  request_id?: string;
  additional_messages?: DatevEauAdditionalMessage[];
  requires_reconnect?: boolean;
}

export interface DatevEauOperationOptions {
  customerId: string;
  clientId: string;                // "berater-mandant" Format
  retrievedBy?: string;
}

// ============================================
// UBERSETZUNGEN DER flag_current_work_incapacity
// ============================================

export const DATEV_EAU_FLAG_LABELS: Record<DatevEauFlag, string> = {
  1: 'Krankenkasse nicht zuständig',
  2: 'Arbeitsunfähigkeit',
  3: 'Krankenhausbehandlung',
  4: 'eAU oder Krankenhausmeldung liegt nicht vor',
  5: 'Rehabilitation / Vorsorge',
  6: 'Teilstationäre Krankenhausbehandlung',
  7: 'In Prüfung',
  8: 'Anderer Nachweis liegt vor',
  9: 'Weiterleitungsverfahren'
};

export const DATEV_EAU_FLAG_COLORS: Record<DatevEauFlag, { bg: string; fg: string }> = {
  1: { bg: '#FEF3C7', fg: '#92400E' },  // KV nicht zuständig — gelb
  2: { bg: '#FEE2E2', fg: '#991B1B' },  // AU — rot
  3: { bg: '#FEE2E2', fg: '#991B1B' },  // Krankenhaus — rot
  4: { bg: '#E5E7EB', fg: '#374151' },  // liegt nicht vor — grau
  5: { bg: '#DBEAFE', fg: '#1E40AF' },  // Reha — blau
  6: { bg: '#FEE2E2', fg: '#991B1B' },  // teilstationär — rot
  7: { bg: '#FEF3C7', fg: '#92400E' },  // in Prüfung — gelb
  8: { bg: '#F0FDF4', fg: '#166534' },  // anderer Nachweis — grün
  9: { bg: '#DBEAFE', fg: '#1E40AF' }   // Weiterleitung — blau
};

// ============================================
// ANSPRECHPARTNER FUER RUECKFRAGEN DER KRANKENKASSE
// ============================================

/**
 * Telefonnummer des Ansprechpartners, den die Krankenkasse bei Rückfragen
 * kontaktiert. Bewusst als Konstante und NICHT aus invoice_settings.firma_telefon
 * gelesen: der dort gepflegte Wert ist nicht wählbar (Landesvorwahl kombiniert mit
 * führender Null) und entspricht nicht der DATEV-Vorgabe, die im Feld telephone
 * ausschliesslich Ziffern und ein führendes Plus erlaubt.
 * Adresse und Firmenname werden weiterhin aus invoice_settings gelesen.
 */
export const XTAX_EAU_TELEFON = '+492216505419800';

/**
 * E-Mail des Ansprechpartners. Bewusst das Sammelpostfach, damit Rückfragen der
 * Krankenkassen im Ticketsystem landen und nicht bei einer Einzelperson.
 */
export const XTAX_EAU_KONTAKT_EMAIL = 'lohn@xtax.de';

/** Vorbelegung der Benachrichtigungsadresse für neue Anfragen. */
export const XTAX_EAU_BENACHRICHTIGUNG_EMAIL = 'lohn@xtax.de';

/**
 * Bereinigt Textfelder des Ansprechpartners auf die von DATEV zugelassenen Zeichen.
 * Laut Fehlermeldung EAU V-RE-CONT002 sind nur Buchstaben, Ziffern sowie
 * Bindestrich, Apostroph, Klammern und Punkt erlaubt; Leerzeichen ebenfalls.
 * Das kaufmännische Und wird durch "und" ersetzt, statt es ersatzlos zu streichen,
 * weil es in Firmierungen bedeutungstragend ist (aus "XTAX GmbH & Co. KG" wird
 * "XTAX GmbH und Co. KG"). Verifiziert am 19.08.2026: unbereinigt lehnt DATEV
 * die Anfrage vollständig ab, Umlaute sind dagegen unproblematisch.
 */
export function sanitizeEauText(value: string): string {
  return (value || '')
    .replace(/&/g, 'und')
    .replace(/[^A-Za-zÀ-ÖØ-öø-ÿ0-9 \-'().]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Entfernt alle Zeichen, die DATEV im Feld telephone nicht zulässt.
 * Erlaubt sind ausschliesslich Ziffern und ein führendes Plus.
 */
export function sanitizeEauTelefon(value: string): string {
  const trimmed = (value || '').trim();
  const hatPlus = trimmed.startsWith('+');
  const ziffern = trimmed.replace(/\D/g, '');
  return hatPlus ? '+' + ziffern : ziffern;
}

/**
 * Zerlegt eine einzeilige Anschrift in Strasse und Hausnummer.
 * DATEV führt beide getrennt, in invoice_settings stehen sie in einem Feld.
 * Getrennt wird am letzten Leerzeichen, sofern der Teil danach mit einer Ziffer
 * beginnt. Greift die Regel nicht, bleibt der gesamte Wert in der Strasse stehen
 * und die Hausnummer bleibt leer (sie ist bei DATEV optional).
 */
export function splitStrasseUndHausnummer(anschrift: string): { street: string; house_number?: string } {
  const wert = (anschrift || '').trim();
  if (!wert) return { street: '' };
  const position = wert.lastIndexOf(' ');
  if (position <= 0) return { street: wert };
  const moeglicheHausnummer = wert.slice(position + 1);
  if (!/^\d/.test(moeglicheHausnummer)) return { street: wert };
  return {
    street: wert.slice(0, position).trim(),
    house_number: moeglicheHausnummer.slice(0, 9)
  };
}

// ============================================
// SERVICE
// ============================================

class DatevEauService {
  private getApiBaseUrl(): string {
    return 'https://intranet.xtax.de/.netlify/functions/datev-eau';
  }

  private buildQuery(opts: DatevEauOperationOptions): string {
    const params = new URLSearchParams();
    params.append('customerId', opts.customerId);
    if (opts.retrievedBy) params.append('retrievedBy', opts.retrievedBy);
    return '?' + params.toString();
  }

  private async fetchJson<T>(method: string, path: string, query: string, body?: any): Promise<T> {
    const init: RequestInit = {
      method: method,
      headers: body ? { 'Content-Type': 'application/json' } : {}
    };
    if (body) init.body = JSON.stringify(body);

    const response = await netlifyFetch(`${this.getApiBaseUrl()}${path}${query}`, init);
    if (!response.ok) {
      const error: DatevEauApiError = await response.json();
      throw error;
    }
    const text = await response.text();
    if (!text) return {} as T;
    return JSON.parse(text) as T;
  }

  /** Spezial-Fetch für DELETE (HTTP 204 = leerer Body) */
  private async fetchDelete(path: string, query: string): Promise<void> {
    const response = await netlifyFetch(`${this.getApiBaseUrl()}${path}${query}`, { method: 'DELETE' });
    if (!response.ok) {
      const error: DatevEauApiError = await response.json();
      throw error;
    }
  }

  // ============================================
  // BERECHTIGUNGSPRÜFUNG (MUST)
  // ============================================

  /** GET /clients/{client-id} */
  async checkAccess(opts: DatevEauOperationOptions): Promise<{ authorized: boolean }> {
    await this.fetchJson<any>('GET', `/clients/${opts.clientId}`, this.buildQuery(opts));
    return { authorized: true };
  }

  /** Test-Funktion: Berechtigungsprüfung mit custom Berater-/Mandantennr. (Audit-MUST) */
  async checkAccessForClient(
    opts: DatevEauOperationOptions,
    testClientId: string
  ): Promise<{ authorized: boolean }> {
    await this.fetchJson<any>('GET', `/clients/${testClientId}`, this.buildQuery(opts));
    return { authorized: true };
  }

  // ============================================
  // eAU-ANFRAGE ERSTELLEN
  // ============================================

  /** POST /clients/{client-id}/employees/{pnr}/eau-requests */
  async createRequest(
    opts: DatevEauOperationOptions,
    personnelNumber: string,
    request: DatevEauRequestBody
  ): Promise<DatevEauPostResponse> {
    return this.fetchJson<DatevEauPostResponse>(
      'POST',
      `/clients/${opts.clientId}/employees/${personnelNumber}/eau-requests`,
      this.buildQuery(opts),
      request
    );
  }

  // ============================================
  // FEEDBACK ABRUFEN
  // ============================================

  /** GET /clients/{client-id}/employees/{pnr}/eau-requests/{uuid}/feedbacks */
  async getFeedback(
    opts: DatevEauOperationOptions,
    personnelNumber: string,
    eauUuid: string
  ): Promise<DatevEauFeedback> {
    return this.fetchJson<DatevEauFeedback>(
      'GET',
      `/clients/${opts.clientId}/employees/${personnelNumber}/eau-requests/${eauUuid}/feedbacks`,
      this.buildQuery(opts)
    );
  }

  // ============================================
  // eAU-ANFRAGE STORNIEREN
  // ============================================

  /** DELETE /clients/{client-id}/employees/{pnr}/eau-requests/{uuid} */
  async cancelRequest(
    opts: DatevEauOperationOptions,
    personnelNumber: string,
    eauUuid: string
  ): Promise<void> {
    await this.fetchDelete(
      `/clients/${opts.clientId}/employees/${personnelNumber}/eau-requests/${eauUuid}`,
      this.buildQuery(opts)
    );
  }
}

export const datevEauService = new DatevEauService();
